import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export default function Settings() {
  const [fromName, setFromName] = useState('');
  const [override, setOverride] = useState('');
  const [theme, setTheme] = useState('system');
  const [secrets, setSecrets] = useState<any>(null);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .settings()
      .then((r) => {
        setFromName(r.settings?.default_from_name || '');
        setOverride(r.settings?.preferred_gateway_override || '');
        setTheme(r.settings?.theme || 'system');
        setSecrets(r.secrets);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    await api.updateSettings({
      default_from_name: fromName,
      preferred_gateway_override: override || null,
      theme,
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  if (loading) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-8 max-w-xl">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1">Preferences and API configuration status</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Preferences</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fromName">Default “From” name</Label>
              <Input
                id="fromName"
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                placeholder="SMS Dashboard"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="override">Preferred gateway domain override</Label>
              <Input
                id="override"
                value={override}
                onChange={(e) => setOverride(e.target.value)}
                placeholder="e.g. vtext.com (optional)"
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Forces this domain for all sends unless a full gateway email is provided.
              </p>
            </div>
            <Button type="submit">{saved ? 'Saved!' : 'Save settings'}</Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">API / email status</CardTitle>
          <CardDescription>
            Secrets stay on the Worker. Cloudflare Email Service binding is preferred when present;
            Resend is the fallback.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex justify-between items-center">
            <span>Twilio Lookup</span>
            {secrets?.twilioConfigured ? (
              <Badge variant="success">Configured · {secrets.twilioSidLast4}</Badge>
            ) : (
              <Badge variant="destructive">Not set</Badge>
            )}
          </div>
          <div className="flex justify-between items-center">
            <span>Cloudflare Email binding</span>
            {secrets?.cloudflareEmailBound ? (
              <Badge variant="success">Bound (EMAIL)</Badge>
            ) : (
              <Badge variant="secondary">Not bound</Badge>
            )}
          </div>
          <div className="flex justify-between items-center">
            <span>Resend (fallback)</span>
            {secrets?.resendConfigured ? (
              <Badge variant="success">Configured · {secrets.resendLast4}</Badge>
            ) : (
              <Badge variant="secondary">Not set</Badge>
            )}
          </div>
          <div className="flex justify-between items-center">
            <span>Active email provider</span>
            <Badge
              variant={
                secrets?.activeEmailProvider === 'cloudflare'
                  ? 'success'
                  : secrets?.activeEmailProvider === 'resend'
                    ? 'warning'
                    : 'destructive'
              }
            >
              {secrets?.activeEmailProvider || 'none'}
            </Badge>
          </div>
          <div className="flex justify-between items-center">
            <span>From email</span>
            <span className="font-mono text-xs">{secrets?.fromEmail || '—'}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Privacy notice</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>
            This dashboard performs carrier lookups against Twilio Lookup and may send emails to
            carrier-operated email-to-SMS gateways. Do not use it for spam, unsolicited marketing,
            or any activity that violates carrier or legal terms.
          </p>
          <p>
            Lookup and message history are stored in your Cloudflare D1 database for your account
            only. API keys remain server-side.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
